import { observer } from 'mobx-react-lite'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { EditorList } from '@/features/legends/components/EditorList'
import { commitOnEnter } from '@/features/legends/editor/commit-on-enter'
import type {
  AnyRoutingContextSourceEditor,
  RoutingContextSourcesBuilderViewModel,
} from '@/features/legends/data-sources/RoutingContextSourcesBuilderViewModel'
import { RoutingDataSource } from '@/features/legends/data-sources/components/RoutingDataSource'
import type { RoutingContextSourceType } from '@/features/legends/data-sources/routing-context-source-types'
import type { ServiceItemPresenceEditorViewModel } from '@/features/legends/data-sources/sources/ServiceItemPresenceEditorViewModel'
import { AllOrAnyOfSelect } from '@/features/legends/filters/components/AllOrAnyOfSelect'

export interface RoutingDataSourcesContainerProps {
  vm: RoutingContextSourcesBuilderViewModel
}

const testId = 'legend-routing-data-sources'

/**
 * The legend's routing data sources, edited in place.
 */
export const RoutingDataSourcesContainer = observer(
  function RoutingDataSourcesContainer({
    vm,
  }: RoutingDataSourcesContainerProps) {
    return (
      <FormGroupContainer error={vm.validation.getError('sources')}>
        <EditorList<RoutingContextSourceType>
          isSpaced
          isDisabled={vm.isEditingDisabled}
          addLabel="Add data source"
          addOptions={vm.availableSourceTypes.map((t) => ({
            id: t.type,
            title: t.friendlyType,
            description: t.friendlyDescription,
          }))}
          onAddOption={vm.addSource}
          testId={testId}
        >
          {vm.sources.map((sourceVm, index) => (
            <RoutingDataSource
              key={sourceVm.cid}
              onRemove={() => vm.remove(sourceVm)}
              isDisabled={sourceVm.isEditingDisabled}
              testId={`${testId}-${index}`}
            >
              <SourceFieldsContainer
                vm={sourceVm}
                testId={`${testId}-${index}`}
              />
            </RoutingDataSource>
          ))}
        </EditorList>
      </FormGroupContainer>
    )
  },
)

interface SourceFieldsContainerProps {
  vm: AnyRoutingContextSourceEditor
  testId?: string
}

const SourceFieldsContainer = observer(function SourceFieldsContainer({
  vm,
  testId,
}: SourceFieldsContainerProps) {
  switch (vm.type) {
    case 'ServiceItemPresence':
      return <ServiceItemPresenceContainer vm={vm} testId={testId} />
    case 'UserWorkRequestCount':
      return (
        <SourceIdContainer
          vm={vm}
          label="User work request count as"
          testId={testId && `${testId}-id`}
        />
      )
  }
})

interface SourceIdContainerProps {
  vm: AnyRoutingContextSourceEditor
  label: string
  testId?: string
}

const SourceIdContainer = observer(function SourceIdContainer({
  vm,
  label,
  testId,
}: SourceIdContainerProps) {
  return (
    <TextInputContainer
      model={vm}
      field="idDraft"
      label={label}
      error={vm.validation.getError('id')}
      onBlur={vm.commitId}
      onKeyDown={commitOnEnter(vm.commitId)}
      isDisabled={vm.isEditingDisabled}
      data-test={testId}
    />
  )
})

interface ServiceItemPresenceContainerProps {
  vm: ServiceItemPresenceEditorViewModel
  testId?: string
}

const ServiceItemPresenceContainer = observer(
  function ServiceItemPresenceContainer({
    vm,
    testId,
  }: ServiceItemPresenceContainerProps) {
    return (
      <>
        <SourceIdContainer
          vm={vm}
          label="Service item presence as"
          testId={testId && `${testId}-id`}
        />
        <AllOrAnyOfSelect
          subject="Job"
          noun="service items"
          op={vm.op}
          onOpChange={vm.setOp}
          options={vm.availableInventoryItems}
          value={vm.inventoryItemIds}
          onChange={vm.setInventoryItems}
          placeholder="Select inventory items"
          error={vm.validation.getError('inventoryItemIds')}
          isDisabled={vm.isEditingDisabled}
          aria-label="Presence operation"
          testId={testId}
          optionsTestId={testId && `${testId}-inventory-items`}
        />
      </>
    )
  },
)
